/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { useBeforeUnload } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { globalAlert } from "../../actions/commonActions";
import { lmsSaveQuizApi, lmsGetQuizApi } from "../../admin/_lmsActions";
import LmsQuizRunner from "./lmsQuizRunner";
import ConfirmationModal from "../_common/confirmModal";
import {
  isQuizQuestionComplete as isComplete,
  isQuizQuestionStarted as isStarted,
  prepareQuizQuestionsForSave,
} from "./lmsQuizFormUtils";

const emptyOption = (isCorrect = false) => ({ text: "", isCorrect, hint: "" });
const emptyQuestion = () => ({
  questionText: "",
  marks: 1,
  explanation: "",
  questionType: "single_choice",
  allowMultiple: false,
  options: [emptyOption(true), emptyOption(false)],
});
const DEFAULT_PASS_MARK = 100;
const DEFAULT_MAX_ATTEMPTS = 2;
const DEFAULT_TIME_LIMIT_MINUTES = null;
const DEFAULT_REVEAL_ANSWERS_AFTER = "attempts_exhausted";

const buildQuestionSnapshot = (q = {}) =>
  JSON.stringify({
    questionText: q?.questionText || "",
    marks: String(q?.marks ?? ""),
    explanation: q?.explanation || "",
    questionType: q?.questionType || "single_choice",
    allowMultiple: !!q?.allowMultiple,
    options: (q?.options || []).map((o) => ({
      text: o?.text || "",
      isCorrect: !!o?.isCorrect,
      hint: o?.hint || "",
    })),
  });

const cleanOne = (q) => {
  let keptCorrect = false;
  const questionType = ["single_choice", "multiple_choice", "true_false"].includes(q.questionType)
    ? q.questionType
    : "single_choice";
  const allowMultiple = questionType === "multiple_choice";
  return {
    ...q,
    allowMultiple,
    questionType,
    questionText: (q.questionText || "").trim(),
    explanation: (q.explanation || "").trim(),
    options: (q.options || [])
      .map((o) => {
        const isCorrect = allowMultiple
          ? !!o.isCorrect
          : !!o.isCorrect && !keptCorrect;
        if (isCorrect) keptCorrect = true;
        return {
          ...o,
          text: (o.text || "").trim(),
          isCorrect,
          hint: (o.hint || "").trim(),
        };
      })
      .filter((o) => o.text !== ""),
  };
};
const buildDraftSnapshot = ({
  passMark,
  maxAttempts,
  timeLimitMinutes,
  randomizeQuestions,
  revealAnswersAfter,
  questions,
}) =>
  JSON.stringify({
    passMark: String(passMark ?? ""),
    maxAttempts: String(maxAttempts ?? ""),
    timeLimitMinutes: String(timeLimitMinutes ?? ""),
    randomizeQuestions: !!randomizeQuestions,
    revealAnswersAfter: revealAnswersAfter || DEFAULT_REVEAL_ANSWERS_AFTER,
    questions: (questions || []).map((q) => ({
      questionText: q?.questionText || "",
      marks: String(q?.marks ?? ""),
      explanation: q?.explanation || "",
      questionType: q?.questionType || "single_choice",
      allowMultiple: !!q?.allowMultiple,
      options: (q?.options || []).map((o) => ({
        text: o?.text || "",
        isCorrect: !!o?.isCorrect,
        hint: o?.hint || "",
      })),
    })),
  });
// Inline quiz builder used both inside the Step 2 split view and the Final
// Assessment step (no modal). Questions are editable accordion cards. Drafts
// save only when the admin clicks Save question; quizzes publish with the course.
const LmsQuizForm = (props) => {
  const { t } = useTranslation();
  const {
    scope,
    refId,
    sectionId,
    initialQuiz,
    onSaved,
    moduleTitle,
    sectionTitle,
    registerUnsavedChangesGuard,
    setWizardDirty,
  } = props;

  const [passMark, setPassMark] = useState(DEFAULT_PASS_MARK);
  const [maxAttempts, setMaxAttempts] = useState(DEFAULT_MAX_ATTEMPTS);
  const [randomizeQuestions, setRandomizeQuestions] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [quizId, setQuizId] = useState("");
  const [status, setStatus] = useState("draft");
  const [openIdx, setOpenIdx] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [pendingDeleteQuestionIndex, setPendingDeleteQuestionIndex] = useState(null);

  const isPublished = status === "published";
  const loadedRef = useRef(null); // ref+section we've already hydrated for
  const savingRef = useRef(false); // guards concurrent saves
  const pendingActionRef = useRef(null);
  const savedSnapshotRef = useRef("");
  const nextQuestionIdRef = useRef(1);
  const savedQuestionSnapshotsRef = useRef({});
  const hydrateKey = `${refId || ""}:${sectionId || ""}`;
  const generatedQuizTitle = `${String(moduleTitle || "Module").trim() || "Module"} - ${
    String(sectionTitle || "Part").trim() || "Part"
  } Quiz`;
  const currentSnapshot = useMemo(
    () =>
      buildDraftSnapshot({
        passMark,
        maxAttempts,
        timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
        randomizeQuestions,
        revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
        questions,
      }),
    [passMark, maxAttempts, randomizeQuestions, questions]
  );
  const hasUnsavedChanges = currentSnapshot !== savedSnapshotRef.current;
  const getQuestionLocalId = useCallback((question = {}) => {
    if (question._localId) return question._localId;
    const localId = `q-${nextQuestionIdRef.current++}`;
    question._localId = localId;
    return localId;
  }, []);
  
  const hasQuestionChanges = useCallback(
  (question) => {
    const localId = getQuestionLocalId(question);
    const baseline =
      savedQuestionSnapshotsRef.current[localId] ||
      buildQuestionSnapshot(emptyQuestion());

    const current = buildQuestionSnapshot(question);
    const changed = current !== baseline;


    return changed;
  },
  [getQuestionLocalId]
);

  const closeUnsavedModal = useCallback(() => {
    pendingActionRef.current = null;
    setUnsavedModalOpen(false);
  }, []);
const requestSaveBeforeLeave = useCallback((action) => {

  if (!hasUnsavedChanges) {
    action?.();
    return true;
  }

  pendingActionRef.current = typeof action === "function" ? action : null;
  setUnsavedModalOpen(true);
  return false;
}, [hasUnsavedChanges, currentSnapshot]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [hasUnsavedChanges]
    )
  );

  useEffect(() => {
    if (!registerUnsavedChangesGuard) return undefined;
    registerUnsavedChangesGuard(requestSaveBeforeLeave);
    return () => registerUnsavedChangesGuard(null);
  }, [registerUnsavedChangesGuard, requestSaveBeforeLeave]);

  // Hydrate once per ref/section (avoids clobbering edits when parent reloads).
  useEffect(() => {
    if (!refId || loadedRef.current === hydrateKey) return;
    loadedRef.current = hydrateKey;
    const stub = initialQuiz;
    if (stub?._id || stub?.quizId) loadQuiz(stub._id || stub.quizId);
    else resetBlank();
  }, [hydrateKey, initialQuiz]);

  const resetBlank = () => {
    const nextQuestions = [];
    savedQuestionSnapshotsRef.current = {};
    setPassMark(DEFAULT_PASS_MARK);
    setMaxAttempts(DEFAULT_MAX_ATTEMPTS);
    setRandomizeQuestions(true);
    setQuestions(nextQuestions);
    setQuizId("");
    setStatus("draft");
    savedSnapshotRef.current = buildDraftSnapshot({
      passMark: DEFAULT_PASS_MARK,
      maxAttempts: DEFAULT_MAX_ATTEMPTS,
      timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
      randomizeQuestions: true,
      revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
      questions: nextQuestions,
    });
  };

  const loadQuiz = async (id) => {
    try {
      const res = await lmsGetQuizApi(id);
      if (res?.data?.status === 200) {
        const q = res?.data?.data?.quiz || res?.data?.data || {};
        setQuizId(q?._id || q?.quizId || id);
        setPassMark(q?.passMark ?? DEFAULT_PASS_MARK);
        setMaxAttempts(q?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
        setRandomizeQuestions(q?.randomizeQuestions ?? true);
        setStatus(q?.status || "draft");
        const loadedQuestions = (q?.questions || []).map((qq) => {
          const inferredMultiple = (qq?.options || []).filter((o) => !!o?.isCorrect).length > 1;
          const questionType = ["single_choice", "multiple_choice", "true_false"].includes(qq?.questionType)
            ? qq.questionType
            : inferredMultiple
            ? "multiple_choice"
            : "single_choice";
          const allowMultiple = questionType === "multiple_choice";
          let keptCorrect = false;
          const loadedQuestion = {
            _id: qq?._id,
            questionText: qq?.questionText || "",
            marks: qq?.marks ?? 1,
            explanation: qq?.explanation || "",
            questionType,
            allowMultiple,
            options: (qq?.options || []).map((o) => {
              const isCorrect = allowMultiple
                ? !!o?.isCorrect
                : !!o?.isCorrect && !keptCorrect;
              if (isCorrect) keptCorrect = true;
              return {
                _id: o?._id,
                text: o?.text || "",
                isCorrect,
                hint: o?.hint || "",
              };
            }),
          };
          const localId = getQuestionLocalId(loadedQuestion);
          savedQuestionSnapshotsRef.current[localId] =
            buildQuestionSnapshot(loadedQuestion);
          return loadedQuestion;
        });
        setQuestions(loadedQuestions);
        savedSnapshotRef.current = buildDraftSnapshot({
          passMark: q?.passMark ?? DEFAULT_PASS_MARK,
          maxAttempts: q?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
          timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
          randomizeQuestions: q?.randomizeQuestions ?? true,
          revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
          questions: loadedQuestions,
        });
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
  };

  /* ── payload + explicit save ── */
  const buildQuizData = (publish, qs) => ({
    ...(quizId ? { quizId } : {}),
    scope,
    refId,
    ...(scope === "section" && sectionId ? { sectionId } : {}),
    passMark: Number(passMark) || 0,
    maxAttempts: maxAttempts === "" ? null : Number(maxAttempts),
    timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
    randomizeQuestions,
    revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
    questions: qs.map((q, i) => {
      const c = cleanOne(q);
      return {
        ...(q?._id ? { _id: q._id } : {}),
        order: i + 1,
        questionText: c.questionText,
        questionType: c.questionType,
        marks: Number(q.marks) || 1,
        explanation: c.explanation,
        options: c.options.map((o) => ({
          ...(o?._id ? { _id: o._id } : {}),
          text: o.text,
          isCorrect: !!o.isCorrect,
          hint: o.hint || "",
        })),
      };
    }),
    publish: publish || isPublished,
  });

  const saveQuizData = async (publish) => {
    if (savingRef.current) return null;
    const { questions: startedQuestions, incompleteIndex } = prepareQuizQuestionsForSave(questions);
    if (incompleteIndex >= 0) {
      globalAlert(
        "error",
        t("admin.lms.quiz.completeQuestionBeforeSave", "Complete every started question before saving.")
      );
      return null;
    }
    if (!startedQuestions.length) {
      globalAlert("error", t("admin.lms.preview.quizEmpty"));
      return null;
    }
    const qs = startedQuestions;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const res = await lmsSaveQuizApi(buildQuizData(publish, qs));
      savingRef.current = false;
      setIsSaving(false);
      if (res?.data?.status === 200) {
        setWizardDirty?.(true);
        const q = res?.data?.data?.quiz || res?.data?.data || {};
        if (q?._id) setQuizId(q._id);
        if (q?.status) setStatus(q.status);
        setQuestions(qs);
        qs.forEach((question) => {
          const localId = getQuestionLocalId(question);
          savedQuestionSnapshotsRef.current[localId] =
            buildQuestionSnapshot(question);
        });
        savedSnapshotRef.current = buildDraftSnapshot({
          passMark,
          maxAttempts,
          timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
          randomizeQuestions,
          revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
          questions: qs,
        });
        globalAlert("success", res?.data?.message);
        onSaved?.(q);
        return q;
      }
      globalAlert("error", res?.data?.message);
      return null;
    } catch (error) {
      savingRef.current = false;
      setIsSaving(false);
      globalAlert("error", error?.response?.data?.message);
      return null;
    }
  };

  /* ── field setters ── */
  const setF = (setter) => (v) => {
    setter(v);
  };

  /* ── question / option mutations ── */
  const mutate = (fn) => {
    setQuestions((prev) => {
      const next = fn([...prev]);
      return next;
    });
  };
  const addQuestion = () => {
    const newQuestion = emptyQuestion();
    const localId = getQuestionLocalId(newQuestion);
    savedQuestionSnapshotsRef.current[localId] =
      buildQuestionSnapshot(emptyQuestion());
    mutate((q) => [...q, newQuestion]);
    setOpenIdx(questions.length);
  };
  const requestRemoveQuestion = (qi) => {
    setPendingDeleteQuestionIndex(qi);
  };
const confirmRemoveQuestion = async () => {
  if (pendingDeleteQuestionIndex == null) return;

  const deletedQuestion = questions[pendingDeleteQuestionIndex];

  const updatedQuestions = questions.filter(
    (_, i) => i !== pendingDeleteQuestionIndex
  );

  const success = await saveDeletedQuestion(updatedQuestions);

  if (!success) return;

  // Remove deleted question snapshot
  if (deletedQuestion?._localId) {
    delete savedQuestionSnapshotsRef.current[deletedQuestion._localId];
  }

  // Update UI only after successful save
  setQuestions(updatedQuestions);

  if (openIdx === pendingDeleteQuestionIndex) {
    setOpenIdx(-1);
  } else if (openIdx > pendingDeleteQuestionIndex) {
    setOpenIdx((prev) => prev - 1);
  }

  setPendingDeleteQuestionIndex(null);

  globalAlert(
    "success",
    t("admin.lms.quiz.questionDeleted", "Question deleted successfully")
  );
};
  const moveQuestion = (qi, dir) =>
    mutate((q) => {
      const t2 = qi + dir;
      if (t2 < 0 || t2 >= q.length) return q;
      [q[qi], q[t2]] = [q[t2], q[qi]];
      return q;
    });
  const updateQuestion = (qi, key, value) =>
    mutate((q) => {
      q[qi] = { ...q[qi], [key]: value };
      return q;
    });
  const setQuestionType = (qi, value) =>
    mutate((q) => {
      const qq = {
        ...q[qi],
        allowMultiple: value === "multiple_choice",
        questionType: value,
      };
      if (value === "true_false") {
        const existing = qq.options || [];
        const existingCorrectIndex = existing.findIndex((option, index) => index < 2 && option?.isCorrect);
        const correctIndex = existingCorrectIndex >= 0 ? existingCorrectIndex : 0;
        qq.options = [
          { ...emptyOption(true), ...(existing[0] || {}), text: existing[0]?.text || "True", isCorrect: correctIndex === 0 },
          { ...emptyOption(false), ...(existing[1] || {}), text: existing[1]?.text || "False", isCorrect: correctIndex === 1 },
        ];
      } else if (value !== "multiple_choice") {
        let kept = false;
        qq.options = qq.options.map((o) => {
          if (o.isCorrect && !kept) {
            kept = true;
            return o;
          }
          return { ...o, isCorrect: false };
        });
      }
      q[qi] = qq;
      return q;
    });
  const updateOption = (qi, oi, key, value) =>
    mutate((q) => {
      const opts = [...q[qi].options];
      if (key === "isCorrect" && !q[qi].allowMultiple) {
        opts.forEach((o, i) => (opts[i] = { ...o, isCorrect: i === oi }));
      } else {
        opts[oi] = { ...opts[oi], [key]: value };
      }
      q[qi] = { ...q[qi], options: opts };
      return q;
    });
  const addOption = (qi) =>
    mutate((q) => {
      q[qi] = { ...q[qi], options: [...q[qi].options, emptyOption(false)] };
      return q;
    });
  const removeOption = (qi, oi) =>
    mutate((q) => {
      q[qi] = { ...q[qi], options: q[qi].options.filter((_, i) => i !== oi) };
      return q;
    });

  /* Manual save stores drafts; quizzes go live with the course. */

  const saveQuiz = async () => {
    return saveQuizData(isPublished);
  };

  const cancelBlockedNavigation = () => {
    closeUnsavedModal();
  };
  const saveDeletedQuestion = async (updatedQuestions) => {
  const incompleteQuestion = updatedQuestions.find(
    (question) => isStarted(question) && !isComplete(question)
  );
  if (incompleteQuestion) {
    globalAlert(
      "error",
      t("admin.lms.quiz.completeQuestionBeforeSave", "Complete every started question before saving.")
    );
    return false;
  }
  try {
    const res = await lmsSaveQuizApi(
      buildQuizData(isPublished, updatedQuestions.filter(isStarted))
    );

    if (res?.data?.status === 200) {
      const q = res?.data?.data?.quiz || res?.data?.data || {};

      setWizardDirty?.(true);

      if (q?._id) setQuizId(q._id);
      if (q?.status) setStatus(q.status);

      // Reset question snapshots
      savedQuestionSnapshotsRef.current = {};

      updatedQuestions.forEach((question) => {
        const localId = getQuestionLocalId(question);
        savedQuestionSnapshotsRef.current[localId] =
          buildQuestionSnapshot(question);
      });

      // Reset quiz snapshot
      savedSnapshotRef.current = buildDraftSnapshot({
        passMark,
        maxAttempts,
        timeLimitMinutes: DEFAULT_TIME_LIMIT_MINUTES,
        randomizeQuestions,
        revealAnswersAfter: DEFAULT_REVEAL_ANSWERS_AFTER,
        questions: updatedQuestions,
      });

      onSaved?.(q);

      return true;
    }

    globalAlert("error", res?.data?.message);
    return false;
  } catch (err) {
    globalAlert("error", err?.response?.data?.message);
    return false;
  }
};


  return (
    <div className="lms-quiz-form">
      {/* Settings */}
      <div className="lms-quiz-settings">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <h4 className="lms-section-title mb-0">
            {t("admin.lms.quiz.builderTitle")}
          </h4>
          {/* {saveLabel && (
            <span className={`lms-save-state lms-save-state-${saveState}`}>
              {saveState === "saving" && (
                <i className="fas fa-circle-notch fa-spin mr-1"></i>
              )}
              {saveState === "saved" && <i className="fas fa-check mr-1"></i>}
              {saveLabel}
            </span>
          )} */}
        </div>

        {isPublished && (
          <div className="lms-published-warning mb-3">
            <i className="fas fa-info-circle mr-2"></i>
            This quiz is published. Save the course to apply your changes to learners.
          </div>
        )}

        <div className="row form-own">
          <div className="form-own-group col-md-6">
            <label className="form-own-label">
              {t("admin.lms.quiz.passMark")} (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              className="form-control"
              value={passMark}
              onChange={(e) => setF(setPassMark)(e.target.value)}
            />
          </div>
          <div className="form-own-group col-md-6">
            <label className="form-own-label">
              {t("admin.lms.quiz.maxAttempts")}
            </label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={maxAttempts}
              onChange={(e) => setF(setMaxAttempts)(e.target.value)}
              // placeholder={t("admin.lms.quiz.unlimited")}
            />
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center">
          <input
            id={`rand-${refId}`}
            type="checkbox"
            checked={randomizeQuestions}
            onChange={(e) => setF(setRandomizeQuestions)(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor={`rand-${refId}`} className="mb-0 form-own-label">
            {t("admin.lms.quiz.randomize")}
          </label>
          </div>
          <button
            type="button"
            className="btn secondary-btn btn-with-icon"
            disabled={isSaving || !hasUnsavedChanges}
            onClick={saveQuiz}
          >
            <i className="fas fa-save me-2"></i>
            {t("admin.lms.quiz.saveSettings", "Save quiz settings")}
          </button>
        </div>
      </div>

      {/* Question accordion list */}
      {questions.length === 0 && (
        
        <div className="lms-question-add">
  <div className="lms-question-add__icon">
    <div className="lms-part-empty-art">
      <span className="lms-part-empty-cloud">
        <i className="fas fa-question-circle"></i>
      </span>
      <span className="lms-part-empty-frame">
        <i className="fas fa-clipboard-check"></i>
      </span>
      <span className="lms-part-empty-plant"></span>
    </div>
  </div>

  <div className="lms-question-add__content">
    <h5>{t("admin.lms.quiz.noQuestions")}</h5>

    <p className="text-muted">
      {t(
        "admin.lms.quiz.noQuestionsDescription",
        "Start by adding your first quiz question."
      )}
    </p>

    <button type="button" className="btn secondary-btn modal-btn btn-with-icon" onClick={addQuestion}>
            <i className="fas fa-plus"></i> {t("admin.lms.quiz.addQuestion")}
          </button>
  </div>
</div>
      )}
                    
      {questions.map((q, qi) => {
        const open = openIdx === qi;
        return (
          <div className={`lms-q-card ${open ? "open" : ""}`} key={qi}>
            <div className="lms-q-head">
              <button
                type="button"
                className="lms-q-head-main"
                onClick={() => setOpenIdx(open ? -1 : qi)}
                aria-expanded={open}
              >
                <span className="lms-q-num">{qi + 1}</span>
                <span className="lms-q-preview">
                  {q.questionText?.trim() || t("admin.lms.quiz.untitledQuestion")}
                </span>
              </button>
              <div
                className="lms-q-toolbar"
              >
                <button
                  type="button"
                  className="btn lms-q-action-btn"
                  disabled={qi === 0}
                  onClick={() => moveQuestion(qi, -1)}
                  title={t("admin.lms.quiz.moveUp")}
                >
                  <i className="fas fa-chevron-up"></i>
                </button>
                <button
                  type="button"
                  className="btn lms-q-action-btn"
                  disabled={qi === questions.length - 1}
                  onClick={() => moveQuestion(qi, 1)}
                  title={t("admin.lms.quiz.moveDown")}
                >
                  <i className="fas fa-chevron-down"></i>
                </button>
                <button
                  type="button"
                  className="btn lms-q-action-btn is-danger"
                  onClick={() => requestRemoveQuestion(qi)}
                  title={t("admin.lms.quiz.deleteQuestion")}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {open && (
              <div className="lms-q-body">
                <input
                  className="form-control mb-3 lms-q-input"
                  placeholder={t("admin.lms.quiz.questionText")}
                  value={q.questionText}
                  onChange={(e) =>
                    updateQuestion(qi, "questionText", e.target.value)
                  }
                />

                <div className="form-own-group mb-3">
                  <label className="form-own-label">
                    {t("admin.lms.quiz.questionType", "Question type")}
                  </label>
                  <select
                    className="form-control"
                    value={q.questionType || "single_choice"}
                    onChange={(e) => setQuestionType(qi, e.target.value)}
                  >
                    <option value="single_choice">
                      {t("admin.lms.quiz.singleChoice", "Single choice")}
                    </option>
                    <option value="multiple_choice">
                      {t("admin.lms.quiz.multipleChoice", "Multiple choice")}
                    </option>
                    <option value="true_false">
                      {t("admin.lms.quiz.trueFalse", "True / False")}
                    </option>
                  </select>
                </div>

                {q.options.map((o, oi) => (
                  <div className="lms-quiz-option mb-2" key={oi}>
                    <div className="lms-quiz-option-rail">
                      <input
                        type={q.questionType === "multiple_choice" ? "checkbox" : "radio"}
                        name={`q-${refId}-${qi}`}
                        checked={!!o.isCorrect}
                        onChange={(e) =>
                          updateOption(
                            qi,
                            oi,
                            "isCorrect",
                            q.questionType === "multiple_choice" ? e.target.checked : true
                          )
                        }
                        className="lms-quiz-option-control"
                      />
                    </div>
                    <div className="lms-quiz-option-content">
                      <input
                        className="form-control mb-1"
                        placeholder={`${t("admin.lms.quiz.option")} ${oi + 1}`}
                        value={o.text}
                        onChange={(e) =>
                          updateOption(qi, oi, "text", e.target.value)
                        }
                      />
                      <input
                        className="form-control mb-1"
                        placeholder={
                          o.isCorrect
                            ? t(
                                "admin.lms.quiz.correctExplanation",
                                "Explain why this answer is correct"
                              )
                            : t(
                                "admin.lms.quiz.wrongExplanation",
                                "Explain why this answer is wrong"
                              )
                        }
                        value={o.hint || ""}
                        onChange={(e) =>
                          updateOption(qi, oi, "hint", e.target.value)
                        }
                      />
                    </div>
                    {q.questionType !== "true_false" && q.options.length > 2 && (
                      <button
                        type="button"
                        className="btn lms-q-option-remove-btn"
                        onClick={() => removeOption(qi, oi)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm secondary-btn mt-1 lms-q-add-option-btn"
                  onClick={() => addOption(qi)}
                  disabled={q.questionType === "true_false"}
                >
                  + {t("admin.lms.quiz.addOption")}
                </button>

                <div className="row align-items-end mt-3">
                  <div className="col-md-3 lms-q-marks-col">
                    <label className="small text-muted">
                      {t("admin.lms.quiz.marks")}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={q.marks}
                      onChange={(e) => updateQuestion(qi, "marks", e.target.value)}
                    />
                  </div>

                  <div className="col-md-8 text-end">
                    <button
                      type="button"
                      className="btn secondary-btn btn-with-icon lms-q-save-btn"
                      disabled={isSaving || !hasQuestionChanges(q)}
                      onClick={() => saveQuiz()}
                    >
                      <i className="fas fa-save me-2"></i>
                      {t("admin.lms.quiz.saveQuiz", "Save question")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {questions.length > 0 && (
  <div className="d-flex justify-content-between align-items-center flex-wrap ms-2">
    <div className="d-flex gap-4">
      <button
        type="button"
        className="btn secondary-btn modal-btn btn-with-icon mt-8"
        onClick={addQuestion}
      >
        <i className="fas fa-plus"></i> {t("admin.lms.quiz.addQuestion")}
      </button>

      {/* <button
        type="button"
        className="btn secondary-btn btn-own-grey btn-with-icon"
        onClick={() => {
          if (!questions.filter(isComplete).length) {
            globalAlert("error", t("admin.lms.preview.quizEmpty"));
            return;
          }
          setPreviewOpen(true);
        }}
      >
        <i className="fas fa-eye mr-1"></i>
        {t("admin.lms.preview.previewQuiz")}
      </button> */}

      {/* <button
        type="button"
        className="btn secondary-btn btn-with-icon"
        disabled={isSaving}
        onClick={saveQuiz}
      >
        <i className="fas fa-save"></i>
        {t("admin.lms.quiz.saveQuiz", "Save question")}
      </button> */}
    </div>
  </div>
)}

      {previewOpen && (
        <Modal
          showCloseIcon
          classNames={{ modal: "admin-modal lms-quiz-modal", root: "modal-own-wrapper" }}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          center
        >
          <div>
            <p className="admin-banner">{t("admin.lms.preview.previewQuiz")}</p>
          </div>
          <div className="px-3 pb-2">
            <div className="lms-preview-banner mb-3">
              <div>
                <i className="fas fa-eye mr-2"></i>
                {t("admin.lms.preview.notSaved")}
              </div>
            </div>
            <LmsQuizRunner
              quiz={{
                title: generatedQuizTitle,
                passMark,
                questions: questions.filter(isComplete),
              }}
            />
          </div>
        </Modal>
      )}

      <Modal
        showCloseIcon={false}
        classNames={{
          modal: "modal-own",
          root: "modal-own-wrapper confirm-modal",
        }}
        open={unsavedModalOpen}
        onClose={cancelBlockedNavigation}
        center
      >
        <div className="video-player-wrapper px-2 text-center">
          <h3 className="font-20 primary-color py-3">
            {t(
              "admin.lms.quiz.unsavedChangesTitle",
              "Save this question before switching"
            )}
          </h3>
          <div className="primary-color">
            <p className="mb-0">
              {t(
                "admin.lms.quiz.unsavedChangesBody",
                "You have unsaved quiz changes. Please save the question first, then continue."
              )}
            </p>
          </div>
          <div className="border-bottom-own pt-2 mb-3"></div>
          <div className="pb-3 btn-wrapper pt-3">
            <button
              type="button"
              onClick={cancelBlockedNavigation}
              className="btn primary-btn full-width-xs btn-own btn-own-primary min-height-btn min-width-btn-md"
            >
              {t("admin.lms.quiz.keepEditing", "Keep editing")}
            </button>
          </div>
        </div>
      </Modal>

      {pendingDeleteQuestionIndex != null && (
        <ConfirmationModal
          open={pendingDeleteQuestionIndex != null}
          deleteInfo={[
            t("admin.lms.quiz.questionNumber", "Question {{n}}", {
              n: pendingDeleteQuestionIndex + 1,
            }),
          ]}
          description={t(
            "admin.quiz.confirmDeleteQuestion",
            "Do you want to delete this question?"
          )}
          onCloseModal={(ok) =>
            ok ? confirmRemoveQuestion() : setPendingDeleteQuestionIndex(null)
          }
        />
      )}

    </div>
  );
};

export default LmsQuizForm;
